import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Edit, Trash2, FileText, Globe, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../context/AdminContext';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    ['clean']
  ]
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'align',
  'blockquote', 'code-block', 'link', 'image'
];

const AdminPages = () => {
  const { toast } = useToast();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [activeTab, setActiveTab] = useState('en');
  const [formData, setFormData] = useState({
    title_en: '', title_ar: '', slug: '', content_en: '', content_ar: '',
    page_type: 'page', meta_title: '', meta_title_ar: '', meta_description: '', meta_description_ar: '', icon: '', order: '0'
  });

  const loadPages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/pages');
      setPages(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load pages', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadPages(); }, [loadPages]);

  const resetForm = () => {
    setEditingPage(null);
    setEditing(false);
    setActiveTab('en');
    setFormData({ title_en: '', title_ar: '', slug: '', content_en: '', content_ar: '', page_type: 'page', meta_title: '', meta_title_ar: '', meta_description: '', meta_description_ar: '', icon: '', order: '0' });
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      title_en: page.title?.en || '', title_ar: page.title?.ar || '', slug: page.slug,
      content_en: page.content?.en || '', content_ar: page.content?.ar || '',
      page_type: page.page_type, meta_title: page.meta_title || '',
      meta_title_ar: page.meta_title_ar || '', meta_description: page.meta_description || '',
      meta_description_ar: page.meta_description_ar || '', icon: page.icon || '',
      order: page.order?.toString() || '0'
    });
    setEditing(true);
  };

  const generateSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        title: { en: formData.title_en, ar: formData.title_ar },
        slug: formData.slug || generateSlug(formData.title_en),
        content: { en: formData.content_en, ar: formData.content_ar },
        page_type: formData.page_type,
        meta_title: formData.meta_title,
        meta_title_ar: formData.meta_title_ar,
        meta_description: formData.meta_description,
        meta_description_ar: formData.meta_description_ar,
        icon: formData.icon,
        order: parseInt(formData.order) || 0,
        is_active: true
      };
      if (editingPage) {
        await adminApi.put(`/pages/${editingPage.id}`, data);
        toast({ title: 'Success', description: 'Page updated' });
      } else {
        await adminApi.post('/pages', data);
        toast({ title: 'Success', description: 'Page created' });
      }
      resetForm();
      loadPages();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save page';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      await adminApi.delete(`/pages/${id}`);
      toast({ title: 'Success', description: 'Page deleted' });
      loadPages();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  // Editor view
  if (editing) {
    return (
      <div data-testid="admin-page-editor">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={resetForm} data-testid="back-to-pages-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />Back
          </Button>
          <h2 className="text-2xl font-bold text-gray-900">{editingPage ? 'Edit Page' : 'Create Page'}</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Title (English) *</Label><Input value={formData.title_en} onChange={(e) => setFormData({ ...formData, title_en: e.target.value, slug: !editingPage ? generateSlug(e.target.value) : formData.slug })} required data-testid="page-title-en" /></div>
            <div><Label>Title (Arabic)</Label><Input value={formData.title_ar} onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })} dir="rtl" data-testid="page-title-ar" /></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><Label>URL Slug</Label><Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} placeholder="auto-generated" data-testid="page-slug" /></div>
            <div>
              <Label>Page Type</Label>
              <select value={formData.page_type} onChange={(e) => setFormData({ ...formData, page_type: e.target.value })} className="w-full rounded-md border px-3 py-2 text-sm" data-testid="page-type-select">
                <option value="page">Quick Link Page</option>
                <option value="service">Service Page</option>
              </select>
            </div>
            <div><Label>Display Order</Label><Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: e.target.value })} /></div>
          </div>

          {/* Language tabs for content editor */}
          <div>
            <div className="flex gap-1 mb-2 border-b">
              <button type="button" onClick={() => setActiveTab('en')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'en' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid="tab-content-en">
                Content (English)
              </button>
              <button type="button" onClick={() => setActiveTab('ar')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'ar' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid="tab-content-ar">
                Content (Arabic)
              </button>
            </div>

            {activeTab === 'en' && (
              <div data-testid="editor-en">
                <ReactQuill
                  theme="snow"
                  value={formData.content_en}
                  onChange={(val) => setFormData({ ...formData, content_en: val })}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  style={{ minHeight: '300px' }}
                  placeholder="Write your page content here..."
                />
              </div>
            )}
            {activeTab === 'ar' && (
              <div dir="rtl" data-testid="editor-ar">
                <ReactQuill
                  theme="snow"
                  value={formData.content_ar}
                  onChange={(val) => setFormData({ ...formData, content_ar: val })}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  style={{ minHeight: '300px' }}
                  placeholder="اكتب محتوى الصفحة هنا..."
                />
              </div>
            )}
          </div>

          {/* SEO */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">SEO Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Meta Title (English)</Label><Input value={formData.meta_title} onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })} placeholder="Page title for English search results" /></div>
              <div><Label>Meta Title (Arabic)</Label><Input value={formData.meta_title_ar} onChange={(e) => setFormData({ ...formData, meta_title_ar: e.target.value })} placeholder="عنوان الصفحة في نتائج البحث العربية" dir="rtl" /></div>
              <div><Label>Icon (Lucide name)</Label><Input value={formData.icon} onChange={(e) => setFormData({ ...formData, icon: e.target.value })} placeholder="e.g. Monitor, Camera" /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div><Label>Meta Description (English)</Label><Input value={formData.meta_description} onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })} placeholder="Brief description for English search results" /></div>
              <div><Label>Meta Description (Arabic)</Label><Input value={formData.meta_description_ar} onChange={(e) => setFormData({ ...formData, meta_description_ar: e.target.value })} placeholder="وصف مختصر لنتائج البحث العربية" dir="rtl" /></div>
            </div>
          </div>

          <div className="flex gap-2 pt-2 border-t">
            <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="save-page-btn">{editingPage ? 'Update Page' : 'Create Page'}</Button>
            <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
          </div>
        </form>
      </div>
    );
  }

  // List view
  const pagePages = pages.filter(p => p.page_type === 'page');
  const servicePages = pages.filter(p => p.page_type === 'service');

  return (
    <div data-testid="admin-pages-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Pages Manager</h2>
          <p className="text-gray-600 mt-1">Manage quick links and service pages with rich HTML content</p>
        </div>
        <Button className="bg-red-600 hover:bg-red-700" onClick={() => { resetForm(); setEditing(true); }} data-testid="add-page-btn">
          <Plus className="mr-2 h-4 w-4" />Add Page
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div></div>
      ) : (
        <div className="space-y-8">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2"><FileText className="h-5 w-5" />Quick Link Pages ({pagePages.length})</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pagePages.map((page) => (
                <Card key={page.id} data-testid={`page-item-${page.id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-bold text-gray-900">{page.title?.en || page.slug}</h4>
                        <p className="text-xs text-gray-500">/{page.slug}</p>
                      </div>
                      <Badge className="bg-gray-100 text-gray-600">Page</Badge>
                    </div>
                    <p className="text-xs text-gray-400 line-clamp-2 mb-3" dangerouslySetInnerHTML={{ __html: (page.content?.en || '').substring(0, 100) }} />
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(page)} data-testid={`edit-page-${page.slug}`}><Edit className="h-4 w-4 mr-1" />Edit</Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(page.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2"><Globe className="h-5 w-5" />Service Pages ({servicePages.length})</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {servicePages.map((page) => (
                <Card key={page.id} data-testid={`page-item-${page.id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-bold text-gray-900">{page.title?.en || page.slug}</h4>
                        <p className="text-xs text-gray-500">/{page.slug}</p>
                      </div>
                      <Badge className="bg-red-100 text-red-700">Service</Badge>
                    </div>
                    <p className="text-xs text-gray-400 line-clamp-2 mb-3" dangerouslySetInnerHTML={{ __html: (page.content?.en || '').substring(0, 100) }} />
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(page)} data-testid={`edit-page-${page.slug}`}><Edit className="h-4 w-4 mr-1" />Edit</Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(page.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPages;
